import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { brl, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Store, Loader2, CreditCard, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export default function PublicClient() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    api.get(`/public/client/${token}`).then((r) => setData(r.data)).catch((e) => setError(apiError(e.response?.data?.detail)));
  }, [token]);

  const pay = async () => {
    setPaying(true);
    try {
      const { data } = await api.post(`/public/client/${token}/pay`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setPaying(false); }
  };

  if (error) return <Centered><p className="text-slate-500 text-center">{error}</p></Centered>;
  if (!data) return <Centered><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></Centered>;

  return (
    <div className="min-h-screen bg-[#F9F8F6] py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-slate-900 flex items-center justify-center"><Store className="w-5 h-5 text-white" /></div>
          <p className="font-display font-bold text-slate-900">{data.store_name}</p>
        </div>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-200 bg-white p-6 mb-4">
          <p className="text-slate-500">Olá, <span className="font-semibold text-slate-900">{data.customer_name}</span></p>
          <p className="text-slate-400 text-sm mt-4 mb-1">{data.balance > 0 ? "Você deve" : "Você está em dia"}</p>
          <p className={`font-display text-5xl font-black ${data.balance > 0 ? "text-red-600" : "text-green-600"}`} data-testid="public-balance">{brl(data.balance)}</p>

          {data.balance > 0 && (
            <Button onClick={pay} disabled={paying} className="w-full rounded-full font-semibold mt-6 bg-slate-900" style={{ height: 56 }} data-testid="pay-button">
              {paying ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CreditCard className="w-5 h-5" /> Pagar agora</>}
            </Button>
          )}
          {data.balance <= 0 && (
            <div className="flex items-center gap-2 text-green-600 mt-6 font-medium"><CheckCircle2 className="w-5 h-5" /> Nenhuma dívida em aberto</div>
          )}
        </motion.div>

        <h2 className="font-display text-lg font-bold text-slate-900 mb-2 mt-6">Extrato</h2>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {data.transactions.length === 0 && <div className="p-8 text-center text-slate-400">Sem lançamentos.</div>}
          {data.transactions.map((t, i) => (
            <div key={i} className={`flex items-center justify-between px-5 py-3.5 ${i !== data.transactions.length - 1 ? "border-b border-slate-100" : ""}`}>
              <div className="min-w-0"><p className="font-medium text-slate-800">{t.type === "debt" ? (t.product || "Fiado") : "Pagamento"}</p><p className="text-xs text-slate-400 truncate">{t.note || "—"} · {fmtDate(t.created_at)}</p></div>
              <span className={`font-display font-bold ${t.type === "debt" ? "text-red-600" : "text-green-600"}`}>{t.type === "debt" ? "+" : "−"} {brl(t.amount)}</span>
            </div>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Pagamento processado com segurança via Stripe.</p>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9F8F6]">{children}</div>;
}
